import type { ElementState } from '@wix/ambassador-devcenter-v1-component-type-data/types';
import type { CompRef } from '@wix/document-services-types';
import { ManifestComponentExtensionType } from '../stores';
import { deepMergeObjects, isPlainObject, setByPath } from '../utils';
import { AbstractExternalAPIHandler } from './AbstractExternalAPIHandler';
import { ChangeType } from '../ManifestChangeDetection';
import { DependencyToken } from '../dependencies';
import type { EditorPointer } from '@wix/editor-document-logic-api';

const GLOBAL_ROLLBACK_INSTRUCTIONS = new Map<
  string,
  {
    instructions: RollbackInstructions;
    editorPointer: EditorPointer<CompRef>;
    type: ManifestComponentExtensionType;
  }
>();

interface RollbackInstructions {
  propsToRemove: string[][];
  propsToRestore: Record<string, any>;
}

function calculateRollbackInstructions(
  existingProps: Record<string, any>,
  propsOverrides: Record<string, any>,
  currentPath: string[] = [],
): RollbackInstructions {
  const results = Object.keys(propsOverrides).map((key) => {
    const overrideValue = propsOverrides[key];
    const existingValue = existingProps[key];
    const keyPath = [...currentPath, key];

    if (!(key in existingProps)) {
      return {
        propsToRemove: [keyPath],
        propsToRestore: {},
      };
    }

    if (isPlainObject(overrideValue) && isPlainObject(existingValue)) {
      return calculateRollbackInstructions(
        existingValue,
        overrideValue,
        keyPath,
      );
    }

    if (existingValue !== overrideValue) {
      return {
        propsToRemove: [],
        propsToRestore: setByPath({}, keyPath, existingValue),
      };
    }

    return {
      propsToRemove: [],
      propsToRestore: {},
    };
  });

  return {
    propsToRemove: results.flatMap((r) => r.propsToRemove),
    propsToRestore: deepMergeObjects(results.map((r) => r.propsToRestore)),
  };
}

export class ExternalPropsHandler extends AbstractExternalAPIHandler {
  #getComponentPath(editorPointer: EditorPointer<CompRef>) {
    return [
      this.editorPointerAPI.getDocumentPointer(editorPointer).id,
      ...(this.editorPointerAPI.getInnerElementPath(editorPointer) ?? []),
    ];
  }

  #getEditorPointerUniqueId(editorPointer: EditorPointer<CompRef>) {
    const documentPointer =
      this.editorPointerAPI.getDocumentPointer(editorPointer);
    const innerElementPath =
      this.editorPointerAPI.getInnerElementPath(editorPointer);

    return this.editorPointerAPI.getEditorPointerUniqueId(
      documentPointer,
      innerElementPath,
      { ignoreVariants: true },
    );
  }

  removeProps(
    editorPointer: EditorPointer<CompRef>,
    type: ManifestComponentExtensionType,
    propPath: string[],
  ) {
    switch (type) {
      case ManifestComponentExtensionType.BUILDER:
      case ManifestComponentExtensionType.OOI:
        const path = this.#getComponentPath(editorPointer);

        const removeProps = this.dependenciesContributor.get(
          DependencyToken.PROPS_REMOVE,
        );

        removeProps(path, propPath);

        this.manifestChangeDetection.notify({
          type: ChangeType.Props,
          editorPointer,
          extensionType: type,
          // TODO: should value be all props or just the removed props?
          value: this.getProps(editorPointer, type),
        });

        break;

      default:
        break;
    }
  }

  updateProps(
    editorPointer: EditorPointer<CompRef>,
    type: ManifestComponentExtensionType,
    props: Record<string, unknown>,
  ) {
    switch (type) {
      case ManifestComponentExtensionType.BUILDER:
      case ManifestComponentExtensionType.OOI:
        const path = this.#getComponentPath(editorPointer);

        const updateProps = this.dependenciesContributor.get(
          DependencyToken.PROPS_UPDATE,
        );

        updateProps(path, props);

        this.manifestChangeDetection.notify({
          type: ChangeType.Props,
          editorPointer,
          extensionType: type,
          // TODO: should value be all props or just the updated props?
          value: this.getProps(editorPointer, type),
        });

        break;

      default:
        break;
    }
  }

  getProps(
    editorPointer: EditorPointer<CompRef>,
    type: ManifestComponentExtensionType,
  ) {
    switch (type) {
      case ManifestComponentExtensionType.BUILDER:
      case ManifestComponentExtensionType.OOI:
        const path = this.#getComponentPath(editorPointer);
        const getProps = this.dependenciesContributor.get(
          DependencyToken.PROPS_GET,
        );
        return getProps(path) ?? {};

      default:
        return {};
    }
  }

  updatePropsOverrides(
    editorPointer: EditorPointer<CompRef>,
    type: ManifestComponentExtensionType,
    propsToUpdate: Record<string, unknown>,
  ) {
    switch (type) {
      case ManifestComponentExtensionType.BUILDER:
      case ManifestComponentExtensionType.OOI:
        const updatePropsOverrides = this.dependenciesContributor.get(
          DependencyToken.PROPS_OVERRIDES_UPDATE,
        );

        updatePropsOverrides(editorPointer, propsToUpdate);

        this.manifestChangeDetection.notify({
          type: ChangeType.Props,
          editorPointer,
          extensionType: type,
          value: this.getProps(editorPointer, type),
        });

        break;

      default:
        break;
    }
  }

  getPropsOverrides(
    editorPointer: EditorPointer<CompRef>,
    type: ManifestComponentExtensionType,
  ) {
    switch (type) {
      case ManifestComponentExtensionType.BUILDER:
      case ManifestComponentExtensionType.OOI:
        const getPropsOverrides = this.dependenciesContributor.get(
          DependencyToken.PROPS_OVERRIDES_GET,
        );
        return getPropsOverrides(editorPointer);

      default:
        return undefined;
    }
  }

  removePropsOverrides(
    editorPointer: EditorPointer<CompRef>,
    type: ManifestComponentExtensionType,
    keys?: string[],
  ) {
    switch (type) {
      case ManifestComponentExtensionType.BUILDER:
      case ManifestComponentExtensionType.OOI:
        const removePropsOverrides = this.dependenciesContributor.get(
          DependencyToken.PROPS_OVERRIDES_REMOVE,
        );

        removePropsOverrides(editorPointer, keys);

        this.manifestChangeDetection.notify({
          type: ChangeType.Props,
          editorPointer,
          extensionType: type,
          value: this.getProps(editorPointer, type),
        });

        break;

      default:
        break;
    }
  }

  #rollbackProps(
    editorPointer: EditorPointer<CompRef>,
    type: ManifestComponentExtensionType,
    rollbackInstructions: RollbackInstructions,
  ) {
    rollbackInstructions.propsToRemove.forEach((propPath) => {
      this.removeProps(editorPointer, type, propPath);
    });

    if (Object.keys(rollbackInstructions.propsToRestore).length > 0) {
      this.updateProps(
        editorPointer,
        type,
        rollbackInstructions.propsToRestore,
      );
    }
  }

  async setPropsOverride(
    editorPointer: EditorPointer<CompRef>,
    type: ManifestComponentExtensionType,
    propsOverrides: ElementState['props'],
  ) {
    const id = this.#getEditorPointerUniqueId(editorPointer);

    if (GLOBAL_ROLLBACK_INSTRUCTIONS.has(id)) {
      const rollbackInstructions =
        GLOBAL_ROLLBACK_INSTRUCTIONS.get(id)!.instructions;

      this.#rollbackProps(editorPointer, type, rollbackInstructions);

      GLOBAL_ROLLBACK_INSTRUCTIONS.delete(id);
    }

    if (!propsOverrides || Object.keys(propsOverrides).length === 0) {
      return;
    }

    const waitForChanges = this.dependenciesContributor.get(
      DependencyToken.DM_CHANGES_WAIT,
    );

    await waitForChanges();

    const existingProps = this.getProps(editorPointer, type);

    this.updateProps(editorPointer, type, propsOverrides);

    const rollbackInstructions = calculateRollbackInstructions(
      existingProps || {},
      propsOverrides,
    );

    GLOBAL_ROLLBACK_INSTRUCTIONS.set(id, {
      instructions: rollbackInstructions,
      editorPointer, // editorPointer could be with/out variants, potentially with different rollback instructions?
      type,
    });
  }

  removePropsOverride(
    editorPointer: EditorPointer<CompRef>,
    type: ManifestComponentExtensionType,
  ) {
    const id = this.#getEditorPointerUniqueId(editorPointer);
    const rollbackInstructions =
      GLOBAL_ROLLBACK_INSTRUCTIONS.get(id)?.instructions;

    if (!rollbackInstructions) {
      return;
    }

    this.#rollbackProps(editorPointer, type, rollbackInstructions);
    GLOBAL_ROLLBACK_INSTRUCTIONS.delete(id);
  }

  removeAllPropsOverrides() {
    GLOBAL_ROLLBACK_INSTRUCTIONS.forEach(
      ({ instructions, editorPointer, type }) => {
        this.#rollbackProps(editorPointer, type, instructions);
      },
    );

    GLOBAL_ROLLBACK_INSTRUCTIONS.clear();
  }
}
